import React, {
    cloneElement,
    isValidElement,
    useCallback,
    useEffect,
    useRef,
    useState,
    type ComponentProps,
    type DetailedHTMLProps,
    type InputHTMLAttributes,
    type ReactElement,
    type Ref,
    type FocusEvent,
    type ChangeEvent,
    type KeyboardEvent,
    type CSSProperties
} from 'react';

import { useIsomorphicLayoutEffect } from '../../../../hooks/useIsomorphicLayoutEffect';
import { useMergeRefs } from '../../../../hooks/useMergeRefs';
import isNil from '../../../../utils/isNil';
import { Key } from '../../../../utils/key';
import { omit } from '../../../../utils/omit';
import { combineEventHandlers } from '../../../Floating/Floating.utils';
import { SELECT_TRIGGER_EVENTS } from '../../Select.constants';
import { isFocusTrapped } from '../../Select.helpers';
import { useSelectRootContext } from '../root/SelectRootContext';
import { useSelectRootFloatingContext } from '../root/SelectRootFloatingContext';

type UseSelectInputProps = {
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
    customInput?: React.ReactElement;
};

/**
 * Creates the input node that is rendered inside the select component.
 *
 * Main responsibilities:
 * - Handles the search functionality when in single mode (multiple mode search happens inside the select dropdown).
 * - Serves as positional reference for the dropdown popover.
 * - Provides accessibility attributes.
 *
 * Notes:
 * - Even when not searchable, the input still renders because of its role as a reference element and to maintain the accessibility tree.
 */
export const useSelectInput = ({ onBlur, onFocus, customInput }: UseSelectInputProps = {}) => {
    const canTriggerFocus = useRef(true);
    const {
        id,
        isInlineInputSearchVisible,
        disabled,
        controlledValue: value,
        showSearch,
        internalSearchValue,
        isInputEditable,
        tabIndex,
        autoFocus,
        handleSearch,
        mode,
        open,
        setIsSearchFieldFocused,
        setFocused,
        setOpen,
        setValue,
        setInternalSearchValue,
        options,
        selection,
        ariaProps,
        autoComplete,
        consumerSelectRef
    } = useSelectRootContext();

    const { floatingRefs, floatingInteractions } = useSelectRootFloatingContext();

    const [inputValue, setInputValue] = useState(String(internalSearchValue || ''));

    const selectInputContainerRef = useRef<HTMLInputElement | null>(null);
    const { activeIndex } = options;
    const { selectOptionByIndex } = selection;
    const floatingReferenceProps: ComponentProps<'input'> = omit(
        floatingInteractions.getReferenceProps({
            ...ariaProps
        }),
        SELECT_TRIGGER_EVENTS
    );
    const hasCustomAutoCompleteInput = autoComplete && isValidElement(customInput);
    /**
     * Tries to focus the first input or textarea element in the select's children.
     * We need this because the input is not always the first element passed as children
     * when using `Select` with `autoComplete` and custom input component.
     */
    const focusSearchInput = useCallback(() => {
        let inputElement = selectInputContainerRef?.current;
        if (!inputElement) return;
        if (!['input', 'textarea'].includes(inputElement.tagName.toLowerCase())) {
            inputElement = inputElement.querySelector('input, textarea');
        }
        if (inputElement) {
            inputElement.focus({ preventScroll: true });
        }
    }, []);

    // Auto focus the search input if applicable
    useIsomorphicLayoutEffect(() => {
        if (!autoFocus) return;
        if (autoComplete) {
            focusSearchInput();
            return;
        }
        floatingRefs.domReference.current?.focus();
    }, [autoFocus, autoComplete, floatingRefs.domReference, focusSearchInput]);

    useEffect(() => {
        if (open && isInputEditable) {
            focusSearchInput();
        }
    }, [open, isInputEditable, focusSearchInput]);

    let searchInputNode = (isValidElement(customInput) ? customInput : <input />) as ReactElement<
        DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>
    > & {
        ref?: Ref<HTMLInputElement>;
    };
    const { ref: originRef, props: originProps } = searchInputNode;
    const searchInputNodeHandlers: ComponentProps<'input'> = combineEventHandlers(
        {
            onFocus(e: FocusEvent<HTMLInputElement>) {
                setFocused(true);
                if (canTriggerFocus.current) {
                    onFocus?.(e);
                    canTriggerFocus.current = false;
                }
                setIsSearchFieldFocused(true);
            },
            onBlur(e: FocusEvent<HTMLInputElement>) {
                if (e.relatedTarget === null || !isFocusTrapped(floatingRefs, e.relatedTarget)) {
                    setFocused(false);
                    onBlur?.(e);
                    canTriggerFocus.current = true;
                    if (
                        isInputEditable &&
                        mode === 'single' &&
                        internalSearchValue !== '' &&
                        !autoComplete
                    ) {
                        if (value !== internalSearchValue) {
                            setValue('');
                            setInternalSearchValue('');
                            setInputValue('');
                        } else if (!value) {
                            setValue('');
                            setInternalSearchValue('');
                            setInputValue('');
                        }
                    }
                }
                setIsSearchFieldFocused(false);
            },
            onChange: (e: ChangeEvent<HTMLInputElement>) => {
                if (isInputEditable) {
                    handleSearch(e.target.value);
                    if (showSearch && !autoComplete) {
                        setInputValue(e.target.value);
                        setValue(e.target.value);
                    }
                }
            },
            onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => {
                if (e.key === Key.Space && !open) {
                    if (hasCustomAutoCompleteInput) {
                        setOpen(true);
                    } else {
                        e.preventDefault();
                        setOpen(true);
                    }
                }
                if (isInputEditable) {
                    if (e.key === Key.Enter && !isNil(activeIndex)) {
                        e.preventDefault();
                        selectOptionByIndex(activeIndex);
                    }
                    if (e.key === Key.Escape) {
                        e.preventDefault();
                        setOpen(false);
                    }
                }
            }
        } as ComponentProps<'input'>,
        originProps
    );

    useEffect(() => {
        if (showSearch && !autoComplete) {
            setInputValue(String(internalSearchValue || ''));
        }
    }, [internalSearchValue, showSearch, autoComplete]);

    const searchInputNodeProps: ComponentProps<'input'> = {
        ...(hasCustomAutoCompleteInput ? {} : { type: 'search' }),
        id,
        disabled,
        tabIndex,
        autoFocus,
        readOnly: !isInputEditable,
        ...originProps,
        ...floatingReferenceProps,
        ...combineEventHandlers(searchInputNodeHandlers, floatingReferenceProps),
        'aria-owns': floatingReferenceProps['aria-controls'],
        'aria-autocomplete': isInputEditable ? 'list' : undefined,
        unselectable: !isInputEditable ? 'on' : undefined,
        autoComplete: 'off',
        ref: useMergeRefs([
            originRef,
            floatingRefs.setReference,
            consumerSelectRef,
            selectInputContainerRef
        ]),
        style: {
            ...originProps.style,
            ...(floatingReferenceProps.style as CSSProperties),
            ...(!isInlineInputSearchVisible ? { opacity: 0, cursor: 'inherit' } : {})
        },
        value: (showSearch && !autoComplete ? inputValue : value) ?? ''
    };

    searchInputNode = cloneElement(searchInputNode, searchInputNodeProps);

    return searchInputNode;
};
